import type {
  AssistantActionBase,
  AssistantMessage,
  AssistantPresentationActionLink,
  AssistantPresentationDiagnosticsSection,
  AssistantPresentationModel,
  AssistantPresentationRelatedLinkGroup,
  AssistantPresentationResultCard,
  AssistantPresentationSection,
  AssistantPresentationSectionType,
  AssistantPresentationSourceItem,
  AssistantPresentationSourcesSection,
  AssistantResponseKind,
} from "../types";

const SECTION_ORDER: Record<AssistantPresentationSectionType, number> = {
  summary: 10,
  metric_grid: 14,
  checklist: 18,
  chart: 20,
  table: 22,
  result_cards: 26,
  confirmation: 29,
  artifacts: 28,
  task_proposal: 30,
  task_thread: 35,
  formatted_body: 40,
  sources: 50,
  related_links: 60,
  diagnostics: 70,
};

const PROACTIVE_GUIDE_SECTION_ORDER: Partial<
  Record<AssistantPresentationSectionType, number>
> = {
  result_cards: 16,
};

const MULTI_ARTIFACT_INSIGHT_SECTION_ORDER: Partial<
  Record<AssistantPresentationSectionType, number>
> = {
  result_cards: 16,
  checklist: 24,
};

const resolveSectionOrder = (
  sectionType: AssistantPresentationSectionType,
  responseKind?: AssistantResponseKind | null,
): number => {
  if (responseKind === "proactive_guide") {
    return PROACTIVE_GUIDE_SECTION_ORDER[sectionType] ?? SECTION_ORDER[sectionType];
  }
  if (responseKind === "multi_artifact_insight") {
    return MULTI_ARTIFACT_INSIGHT_SECTION_ORDER[sectionType] ?? SECTION_ORDER[sectionType];
  }
  return SECTION_ORDER[sectionType];
};

const DEFAULT_SOURCES_SECTION: AssistantPresentationSourcesSection = {
  type: "sources",
  id: "shared-default-sources",
  title: "Sources",
  items: [
    {
      id: "shared-default-source",
      label: "Assistant grounding",
      meta: "shared.response-rendering",
      preview:
        "No route-specific citations were attached to this response. Retry from a live route for exact records.",
      title:
        "No route-specific citations were attached to this response. Retry from a live route for exact records.",
    },
  ],
};

const ROUTE_ACTION_PATH_PATTERN =
  /^\/(?:school|administrator|super-administrator|parent)\//;

const MACHINE_PAYLOAD_KEY_PATTERN =
  /"(answer|citations|actions|debug|tenantStatus|dataSourceStatus|recommendation)"\s*:/i;
const MACHINE_PAYLOAD_LINE_PATTERN =
  /^\s*("?(answer|citations|actions|debug|tenantStatus|dataSourceStatus|recommendation)"?\s*:|[{}\[\]],?)\s*$/i;
const CODE_FENCE_PATTERN = /```(?:json|javascript|js|txt)?\s*([\s\S]*?)```/gi;
const JSON_FENCE_OPEN_PATTERN = /```(?:json|javascript|js|txt)?/gi;
const RAW_ROUTE_PATTERN = /\/school\/[a-z0-9/_-]+/i;
const RAW_ID_PATTERN_SOURCE = String.raw`(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|YST-\d{4}-[A-Z0-9]+|FA-\d{4}-\d+|(?=[A-Z0-9]*\d)[A-Z0-9]{5,}|[a-z]{2,}[-_][0-9a-f]{8,}|run[-_][0-9a-f]{8,}|work[-_]?item[-_][0-9a-f]{8,})`;
const RAW_ID_PATTERN = new RegExp(
  String.raw`\b${RAW_ID_PATTERN_SOURCE}\b`,
  "gi",
);
const RAW_ID_PARENTHETICAL_PATTERN = new RegExp(
  String.raw`\s*\([^)]*\b${RAW_ID_PATTERN_SOURCE}\b[^)]*\)`,
  "gi",
);
const RAW_ID_FIELD_PATTERN = new RegExp(
  String.raw`\b(?:application|applicant|record|source|artifact|task|thread|run)?\s*(?:id|code|ref|reference)\s*:\s*\b${RAW_ID_PATTERN_SOURCE}\b`,
  "gi",
);
const ARTIFACT_REF_PATTERN =
  /\b(?:assistant-artifacts|sourceRef|source_ref|data-chart-source-ref|data-workbook-source-ref|artifact[-_]?source[-_]?ref)\b/i;
const DEVELOPMENT_TERM_PATTERN =
  /\b(?:AppSync|mutateAssistantRun|askSchoolAI|askAdmissionsAI|GraphQL|tenantScopeStatus|legacy_placeholder|sourceRef|workItemId|taskThreadId|runId|debug)\b/i;
const FLAT_UI_LABEL_PATTERN =
  /\b(?:ActionsOpen|Route detailsOpen|SourcesRelated|Open applicationOpen|Open workspaceOpen|Task details[A-Z]|Useful next checks[A-Z])\b/;
const MARKDOWN_TABLE_PATTERN = /^\s*\|.+\|\s*$/m;
const MARKDOWN_TABLE_SEPARATOR_PATTERN = /^\s*\|[\s:-]+\|\s*$/m;

export type AssistantPresentationQualityIssue = {
  code: string;
  severity: "error" | "warning";
  sectionType?: AssistantPresentationSectionType | "presentation" | null;
  excerpt?: string;
  message: string;
};

const RESPONSE_KIND_REQUIRED_SECTIONS: Partial<Record<
  AssistantResponseKind,
  AssistantPresentationSectionType[]
>> = {
  text_summary: ["summary"],
  signal_snapshot: ["summary", "metric_grid"],
  target_gallery: ["summary", "result_cards"],
  todo_list: ["summary", "checklist"],
  inline_table: ["summary", "table"],
  inline_chart: ["summary", "chart"],
  multi_artifact_insight: ["summary", "chart", "table", "artifacts"],
  chart_artifact: ["summary", "artifacts"],
  visualization3d_artifact: ["summary", "artifacts"],
  spreadsheet_artifact: ["summary", "artifacts"],
  landing_page_artifact: ["summary", "artifacts"],
  diagram_artifact: ["summary", "artifacts"],
  pdf_generation: ["summary", "artifacts"],
  csv_export: ["summary", "artifacts"],
  xlsx_export: ["summary", "artifacts"],
  source_ledger_export: ["summary", "artifacts"],
  proactive_guide: ["summary", "checklist"],
  navigation_assistance: ["summary"],
  draft_creator: ["summary", "formatted_body"],
  data_unavailable: ["summary"],
};

const RESPONSE_KIND_ANY_OF_SECTIONS: Partial<
  Record<AssistantResponseKind, AssistantPresentationSectionType[]>
> = {
  navigation_assistance: ["result_cards", "table"],
};

const normalizeHeadingComparison = (value: string): string =>
  collapseWhitespace(value)
    .toLowerCase()
    .replace(/[:.]+$/g, "");

const collapseWhitespace = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

const normalizeRouteActionHref = (href: unknown): string => {
  const rawHref = collapseWhitespace(String(href ?? ""));
  if (!rawHref || /^(?:data|blob|mailto|tel):/i.test(rawHref)) return "";
  try {
    const parsed = new URL(rawHref, "https://intellio.local");
    return ROUTE_ACTION_PATH_PATTERN.test(parsed.pathname)
      ? `${parsed.pathname}${parsed.hash}`.toLowerCase()
      : "";
  } catch {
    const [pathAndQuery = ""] = rawHref.split("?");
    const [path = "", hash = ""] = pathAndQuery.split("#");
    return ROUTE_ACTION_PATH_PATTERN.test(path)
      ? `${path}${hash ? `#${hash}` : ""}`.toLowerCase()
      : "";
  }
};

const routeActionKey = (href: unknown): string => normalizeRouteActionHref(href);

const routeActionHrefSuffix = (href: unknown): string =>
  routeActionKey(href).split("/").filter(Boolean).pop()?.replace(/[-_]+/g, " ") ?? "";

const actionContextFragment = (
  card: AssistantPresentationResultCard,
  action: AssistantPresentationActionLink,
): string => {
  const actionLabel = collapseWhitespace(action.label).toLowerCase();
  const candidates = [
    card.label,
    card.eyebrow,
    card.meta,
    card.badge,
    card.preview,
  ];
  const selected = candidates
    .map((value) => collapseWhitespace(String(value ?? "")))
    .find((value) => value && !actionLabel.includes(value.toLowerCase()));
  if (selected) {
    return selected.length > 48 ? `${selected.slice(0, 45).trim()}...` : selected;
  }
  return routeActionHrefSuffix(action.href);
};

const disambiguateResultCardActions = (
  section: Extract<AssistantPresentationSection, { type: "result_cards" }>,
): Extract<AssistantPresentationSection, { type: "result_cards" }> => {
  const labelCounts = new Map<string, number>();
  for (const card of section.cards) {
    [card.primaryAction, card.secondaryAction].forEach((action) => {
      const key = collapseWhitespace(action?.label ?? "").toLowerCase();
      const hrefKey = routeActionKey(action?.href);
      if (key && hrefKey) labelCounts.set(key, (labelCounts.get(key) ?? 0) + 1);
    });
  }

  const rewriteAction = (
    card: AssistantPresentationResultCard,
    action: AssistantPresentationActionLink | null | undefined,
  ): AssistantPresentationActionLink | null | undefined => {
    const key = collapseWhitespace(action?.label ?? "").toLowerCase();
    if (!action || !key || routeActionKey(action.href) === "" || (labelCounts.get(key) ?? 0) <= 1) {
      return action;
    }
    const context = actionContextFragment(card, action);
    if (!context) return action;
    let nextLabel = `${action.label} - ${context}`;
    if (labelCounts.get(nextLabel.toLowerCase()) || labelCounts.get(collapseWhitespace(nextLabel).toLowerCase())) {
      const suffix = routeActionHrefSuffix(action.href);
      nextLabel = suffix ? `${nextLabel} - ${suffix}` : nextLabel;
    }
    labelCounts.set(collapseWhitespace(nextLabel).toLowerCase(), (labelCounts.get(collapseWhitespace(nextLabel).toLowerCase()) ?? 0) + 1);
    return {
      ...action,
      label: nextLabel,
    };
  };

  return {
    ...section,
    cards: section.cards.map((card) => ({
      ...card,
      primaryAction: rewriteAction(card, card.primaryAction) ?? card.primaryAction,
      secondaryAction: rewriteAction(card, card.secondaryAction) ?? card.secondaryAction,
    })),
  };
};

const tableCellText = (
  cell: Extract<AssistantPresentationSection, { type: "table" }>["rows"][number]["cells"][number],
): string => typeof cell === "string" ? collapseWhitespace(cell) : collapseWhitespace(cell.text);

const tableRowContextFragment = (
  row: Extract<AssistantPresentationSection, { type: "table" }>["rows"][number],
  actionCellIndex: number,
): string => {
  const actionText = tableCellText(row.cells[actionCellIndex]).toLowerCase();
  const candidate = row.cells
    .map((cell, index) => ({ text: tableCellText(cell), index }))
    .find(({ text, index }) => index !== actionCellIndex && text && !actionText.includes(text.toLowerCase()));
  if (candidate?.text) return candidate.text.length > 48 ? `${candidate.text.slice(0, 45).trim()}...` : candidate.text;
  return collapseWhitespace(String(row.id ?? "")).replace(/[-_]+/g, " ");
};

const disambiguateTableRouteActionCells = (
  section: Extract<AssistantPresentationSection, { type: "table" }>,
): Extract<AssistantPresentationSection, { type: "table" }> => {
  const labelCounts = new Map<string, number>();
  for (const row of section.rows) {
    row.cells.forEach((cell) => {
      if (typeof cell === "string" || !routeActionKey(cell.href)) return;
      const label = tableCellText(cell).toLowerCase();
      if (label) labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
    });
  }

  const emittedLabels = new Set<string>();
  return {
    ...section,
    rows: section.rows.map((row) => ({
      ...row,
      cells: row.cells.map((cell, index) => {
        if (typeof cell === "string" || !routeActionKey(cell.href)) return cell;
        const label = tableCellText(cell);
        if (!label || (labelCounts.get(label.toLowerCase()) ?? 0) <= 1) return cell;
        const context = tableRowContextFragment(row, index);
        let nextText = context ? `${cell.text} - ${context}` : cell.text;
        const normalizedNext = collapseWhitespace(nextText).toLowerCase();
        if (emittedLabels.has(normalizedNext)) {
          const suffix = routeActionHrefSuffix(cell.href);
          nextText = suffix ? `${nextText} - ${suffix}` : nextText;
        }
        emittedLabels.add(collapseWhitespace(nextText).toLowerCase());
        return {
          ...cell,
          text: nextText,
        };
      }),
    })),
  };
};

const removeDuplicateRouteActionTargets = (
  sections: AssistantPresentationSection[],
): AssistantPresentationSection[] => {
  const seenRouteActionTargets = new Set<string>();
  const relatedLinkRouteActionTargets = new Set<string>();

  sections.forEach((section) => {
    if (section.type !== "related_links") return;
    section.groups.forEach((group) => {
      group.items.forEach((item) => {
        const key = routeActionKey(item.href);
        if (key) relatedLinkRouteActionTargets.add(key);
      });
    });
  });

  const rememberAction = (action: AssistantPresentationActionLink | null | undefined): void => {
    const key = routeActionKey(action?.href);
    if (key) seenRouteActionTargets.add(key);
  };

  const dedupeArtifactItemActions = (
    item: Extract<AssistantPresentationSection, { type: "artifacts" }>["items"][number],
  ): Extract<AssistantPresentationSection, { type: "artifacts" }>["items"][number] => {
    const itemKey = routeActionKey(item.href);
    if (itemKey) seenRouteActionTargets.add(itemKey);
    if (!item.actions?.length) return item;
    const actions = item.actions.filter((action) => {
      const key = routeActionKey(action.href);
      if (!key) return true;
      if (seenRouteActionTargets.has(key) || relatedLinkRouteActionTargets.has(key)) {
        return false;
      }
      seenRouteActionTargets.add(key);
      return true;
    });
    return actions.length === item.actions.length ? item : { ...item, actions };
  };

  return sections
    .map((section): AssistantPresentationSection | null => {
      if (section.type === "result_cards") {
        const disambiguated = disambiguateResultCardActions(section);
        disambiguated.cards.forEach((card) => {
          rememberAction(card.primaryAction);
          rememberAction(card.secondaryAction);
        });
        return disambiguated;
      }
      if (section.type === "table") {
        const disambiguated = disambiguateTableRouteActionCells(section);
        return {
          ...disambiguated,
          rows: disambiguated.rows.map((row) => ({
            ...row,
            cells: row.cells.map((cell) => {
              if (typeof cell === "string") return cell;
              const key = routeActionKey(cell.href);
              if (!key) return cell;
              if (seenRouteActionTargets.has(key) || relatedLinkRouteActionTargets.has(key)) {
                const { href: _href, ...rest } = cell;
                return rest;
              }
              seenRouteActionTargets.add(key);
              return cell;
            }),
          })),
        };
      }
      if (section.type === "artifacts") {
        return {
          ...section,
          items: section.items.map(dedupeArtifactItemActions),
        };
      }
      if (section.type === "related_links") {
        const groups = section.groups
          .map((group) => ({
            ...group,
            items: group.items.filter((item) => {
              const key = routeActionKey(item.href);
              if (!key) return true;
              if (seenRouteActionTargets.has(key)) return false;
              seenRouteActionTargets.add(key);
              return true;
            }),
          }))
          .filter((group) => group.items.length > 0);
        return groups.length > 0 ? { ...section, groups } : null;
      }
      return section;
    })
    .filter((section): section is AssistantPresentationSection => Boolean(section));
};

const PROJECTED_CHECKLIST_SUMMARY_TEXT = "Actionable checks are listed below.";
const GENERIC_CHECKLIST_HEADING_PATTERN =
  /^(?:#{1,6}\s*)?(?:recommended\s+next\s+checks?|response\s+checks?|next\s+steps?|recommended\s+follow-?up|action\s+items?)\s*:?\s*/i;

const stripDuplicatedChecklistHeading = (value: string): string => {
  const compacted = collapseWhitespace(value);
  if (!compacted) {
    return "";
  }
  const withoutHeading = collapseWhitespace(
    compacted.replace(GENERIC_CHECKLIST_HEADING_PATTERN, ""),
  );
  return withoutHeading || PROJECTED_CHECKLIST_SUMMARY_TEXT;
};

const excerpt = (value: string): string =>
  collapseWhitespace(value).slice(0, 160);

const maskVisibleMachineIds = (value: string): string =>
  String(value ?? "")
    .replace(RAW_ID_PARENTHETICAL_PATTERN, "")
    .replace(RAW_ID_FIELD_PATTERN, "")
    .replace(RAW_ID_PATTERN, "")
    .replace(
      /\s*\((?:application|applicant|record|source|artifact|task|thread)\)/gi,
      "",
    )
    .replace(
      /\b(?:assistant-artifacts|sourceRef|source_ref|artifact[-_]?source[-_]?ref):?[^\s,;)]+/gi,
      "artifact reference",
    )
    .replace(/\(\s*\)/g, "")
    .replace(/\s+\b(?:and|or)\s*([.;,])/gi, "$1")
    .replace(/[ \t]{2,}/g, " ");

export const sanitizeVisibleAssistantText = (value: string): string =>
  maskVisibleMachineIds(value)
    .replace(/\bActionsOpen\b/g, "Actions Open")
    .replace(/\bACTIONS\b/g, "Actions")
    .replace(/\bRoute detailsOpen\b/g, "Route details Open")
    .replace(/\bSourcesRelated\b/g, "Sources Related")
    .replace(
      /\b([a-z]+)_([a-z]+(?:_[a-z]+)*)\b/gi,
      (_match, first: string, rest: string) =>
        [first, ...rest.split("_")]
          .map((part) =>
            part
              ? `${part[0]?.toUpperCase() ?? ""}${part.slice(1).toLowerCase()}`
              : "",
          )
          .join(" "),
    )
    .replace(/([a-z)])(\d+\.)/g, "$1\n$2")
    .replace(/([.!?])([A-Z][a-z])/g, "$1 $2")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const isMachinePayload = (value: string): boolean => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith("```")) {
    return true;
  }
  if (MACHINE_PAYLOAD_KEY_PATTERN.test(trimmed)) {
    return true;
  }
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    return true;
  }
  const lines = trimmed
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return (
    lines.length > 0 &&
    lines.every(
      (line) => MACHINE_PAYLOAD_LINE_PATTERN.test(line) || /^".*"$/.test(line),
    )
  );
};

export const stripVisibleAssistantMachinePayload = (value: string): string => {
  if (!value) {
    return "";
  }
  let next = String(value);
  next = next.replace(CODE_FENCE_PATTERN, (full, inner) =>
    isMachinePayload(String(inner ?? "")) ? "\n" : full,
  );
  next = next.replace(JSON_FENCE_OPEN_PATTERN, "");
  next = next
    .split("\n")
    .filter((line) => !MACHINE_PAYLOAD_LINE_PATTERN.test(line.trim()))
    .join("\n");
  return next.replace(/\n{3,}/g, "\n\n").trim();
};

const toSourceItem = (
  citation: unknown,
  index: number,
): AssistantPresentationSourceItem => {
  const candidate =
    citation && typeof citation === "object" && !Array.isArray(citation)
      ? (citation as Record<string, unknown>)
      : {};
  const label =
    collapseWhitespace(
      String(
        candidate.applicationCode ??
          candidate.entityLabel ??
          candidate.label ??
          candidate.table ??
          `Source ${index + 1}`,
      ),
    ) || `Source ${index + 1}`;
  const fieldPath = collapseWhitespace(String(candidate.fieldPath ?? ""));
  const preview = collapseWhitespace(String(candidate.preview ?? "")) || null;
  return {
    id: collapseWhitespace(String(candidate.id ?? `${label}-${index + 1}`))
      .toLowerCase()
      .replace(/\s+/g, "-"),
    label,
    meta: fieldPath || null,
    preview,
    title: preview,
  };
};

const dedupeSourceItems = (
  items: AssistantPresentationSourceItem[],
): AssistantPresentationSourceItem[] => {
  const seen = new Set<string>();
  return items.filter((item, index) => {
    const key =
      `${item.label}::${item.meta ?? ""}::${item.preview ?? ""}`.toLowerCase() ||
      `source-${index + 1}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const dedupeRelatedLinks = (
  groups: AssistantPresentationRelatedLinkGroup[],
): AssistantPresentationRelatedLinkGroup[] =>
  groups
    .map((group) => {
      const seen = new Set<string>();
      return {
        ...group,
        items: group.items.filter((item, index) => {
          const key =
            `${item.href}::${item.label}`.toLowerCase() ||
            `${group.id}-${index + 1}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        }),
      };
    })
    .filter((group) => group.items.length > 0);

export const scanAssistantPresentationTextQuality = (
  text: string,
  input?: {
    sectionType?: AssistantPresentationSectionType | "presentation" | null;
    allowRawRoutes?: boolean;
  },
): AssistantPresentationQualityIssue[] => {
  const normalized = String(text ?? "");
  const issues: AssistantPresentationQualityIssue[] = [];
  const sectionType = input?.sectionType ?? "presentation";
  if (!collapseWhitespace(normalized)) {
    return issues;
  }
  if (
    MARKDOWN_TABLE_PATTERN.test(normalized) &&
    MARKDOWN_TABLE_SEPARATOR_PATTERN.test(normalized)
  ) {
    issues.push({
      code: "raw-markdown-table",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message: "Visible assistant response text contains a raw markdown table.",
    });
  }
  if (/```/.test(normalized)) {
    issues.push({
      code: "fenced-payload",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message:
        "Visible assistant response text contains a markdown/code fence.",
    });
  }
  if (
    /\[[^\]]+\]\((#|https?:\/\/example\.com|todo|tbd|placeholder)[^)]*\)/i.test(
      normalized,
    )
  ) {
    issues.push({
      code: "placeholder-link",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message: "Visible assistant response text contains a placeholder link.",
    });
  }
  if (
    /\b(the links are placeholders|actual urls are not provided|placeholder links?)\b/i.test(
      normalized,
    )
  ) {
    issues.push({
      code: "placeholder-link-copy",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message: "Visible assistant response text describes placeholder links.",
    });
  }
  if (
    /\((?:application|applicant|record|source|artifact|task|thread)\)/i.test(
      normalized,
    )
  ) {
    issues.push({
      code: "generic-entity-placeholder",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message:
        "Visible assistant response text contains a generic entity placeholder instead of a structured link or label.",
    });
  }
  if (!input?.allowRawRoutes && RAW_ROUTE_PATTERN.test(normalized)) {
    issues.push({
      code: "raw-route-leakage",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message: "Visible assistant response text contains a raw route path.",
    });
  }
  const rawIdMatches = normalized.match(RAW_ID_PATTERN) ?? [];
  if (rawIdMatches.length > 0) {
    issues.push({
      code: rawIdMatches.length >= 3 ? "id-heavy-prose" : "raw-id-leakage",
      severity: "error",
      sectionType,
      excerpt: rawIdMatches.slice(0, 5).join(", "),
      message:
        "Visible assistant response text contains raw record or task identifiers.",
    });
  }
  if (ARTIFACT_REF_PATTERN.test(normalized)) {
    issues.push({
      code: "artifact-ref-leakage",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message:
        "Visible assistant response text contains artifact source-reference internals.",
    });
  }
  if (DEVELOPMENT_TERM_PATTERN.test(normalized)) {
    issues.push({
      code: "development-artifact-leakage",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message:
        "Visible assistant response text contains implementation or transport terminology.",
    });
  }
  if (FLAT_UI_LABEL_PATTERN.test(normalized)) {
    issues.push({
      code: "flat-ui-label-concatenation",
      severity: "error",
      sectionType,
      excerpt: excerpt(normalized),
      message:
        "Visible assistant response text has concatenated UI labels instead of structured spacing.",
    });
  }
  return issues;
};

const sanitizeActionLink = <
  T extends { label: string; href?: string | null; actionId?: string | null },
>(
  action: T | null | undefined,
): T | null | undefined => {
  if (!action) {
    return action;
  }
  return {
    ...action,
    label: sanitizeVisibleAssistantText(action.label),
    actionId: action.actionId ?? null,
  };
};

const sanitizePresentationSection = (
  section: AssistantPresentationSection,
): AssistantPresentationSection => {
  if (section.type === "summary" || section.type === "formatted_body") {
    return {
      ...section,
      text: sanitizeVisibleAssistantText(
        stripVisibleAssistantMachinePayload(section.text),
      ),
    };
  }
  if (section.type === "metric_grid") {
    return {
      ...section,
      items: section.items.map((item) => ({
        ...item,
        label: sanitizeVisibleAssistantText(item.label),
        value: sanitizeVisibleAssistantText(item.value),
        detail: item.detail
          ? sanitizeVisibleAssistantText(item.detail)
          : item.detail,
      })),
    };
  }
  if (section.type === "checklist") {
    return {
      ...section,
      items: section.items.map((item) => ({
        ...item,
        label: sanitizeVisibleAssistantText(item.label),
        detail: item.detail
          ? sanitizeVisibleAssistantText(item.detail)
          : item.detail,
      })),
    };
  }
  if (section.type === "result_cards") {
    return {
      ...section,
      cards: section.cards.map((card) => ({
        ...card,
        label: sanitizeVisibleAssistantText(card.label),
        eyebrow: card.eyebrow
          ? sanitizeVisibleAssistantText(card.eyebrow)
          : card.eyebrow,
        badge: card.badge
          ? sanitizeVisibleAssistantText(card.badge)
          : card.badge,
        meta: card.meta ? sanitizeVisibleAssistantText(card.meta) : card.meta,
        preview: card.preview
          ? sanitizeVisibleAssistantText(card.preview)
          : card.preview,
        primaryAction: sanitizeActionLink(card.primaryAction),
        secondaryAction: sanitizeActionLink(card.secondaryAction),
        detailRows: card.detailRows?.map((row) => ({
          label: sanitizeVisibleAssistantText(row.label),
          value: sanitizeVisibleAssistantText(row.value),
        })),
      })),
    };
  }
  if (section.type === "confirmation") {
    return {
      ...section,
      titleText: sanitizeVisibleAssistantText(section.titleText),
      summary: sanitizeVisibleAssistantText(section.summary),
      meta: section.meta
        ? sanitizeVisibleAssistantText(section.meta)
        : section.meta,
    };
  }
  if (section.type === "artifacts") {
    return {
      ...section,
      items: section.items.map((item) => ({
        ...item,
        label: sanitizeVisibleAssistantText(item.label),
        badge: item.badge
          ? sanitizeVisibleAssistantText(item.badge)
          : item.badge,
        meta: item.meta ? sanitizeVisibleAssistantText(item.meta) : item.meta,
        preview: item.preview
          ? sanitizeVisibleAssistantText(item.preview)
          : item.preview,
        actionLabel: item.actionLabel
          ? sanitizeVisibleAssistantText(item.actionLabel)
          : item.actionLabel,
      })),
    };
  }
  if (section.type === "task_proposal") {
    return {
      ...section,
      proposal: {
        ...section.proposal,
        title: sanitizeVisibleAssistantText(section.proposal.title),
        summary: sanitizeVisibleAssistantText(section.proposal.summary),
        question: section.proposal.question
          ? sanitizeVisibleAssistantText(section.proposal.question)
          : section.proposal.question,
        startLabel: section.proposal.startLabel
          ? sanitizeVisibleAssistantText(section.proposal.startLabel)
          : section.proposal.startLabel,
        askInChatLabel: section.proposal.askInChatLabel
          ? sanitizeVisibleAssistantText(section.proposal.askInChatLabel)
          : section.proposal.askInChatLabel,
        dismissLabel: section.proposal.dismissLabel
          ? sanitizeVisibleAssistantText(section.proposal.dismissLabel)
          : section.proposal.dismissLabel,
      },
    };
  }
  if (section.type === "task_thread") {
    return {
      ...section,
      thread: {
        ...section.thread,
        title: sanitizeVisibleAssistantText(section.thread.title),
        stateLabel: sanitizeVisibleAssistantText(section.thread.stateLabel),
        statusMessage: section.thread.statusMessage
          ? sanitizeVisibleAssistantText(section.thread.statusMessage)
          : section.thread.statusMessage,
        detailsSummary: section.thread.detailsSummary
          ? sanitizeVisibleAssistantText(section.thread.detailsSummary)
          : section.thread.detailsSummary,
        degradedReason: section.thread.degradedReason
          ? sanitizeVisibleAssistantText(section.thread.degradedReason)
          : section.thread.degradedReason,
        events: section.thread.events?.map((event) => ({
          ...event,
          label: sanitizeVisibleAssistantText(event.label),
          summary: event.summary
            ? sanitizeVisibleAssistantText(event.summary)
            : event.summary,
          content: event.content
            ? sanitizeVisibleAssistantText(event.content)
            : event.content,
        })),
      },
    };
  }
  if (section.type === "sources") {
    return {
      ...section,
      items: section.items.map((item) => ({
        ...item,
        label: sanitizeVisibleAssistantText(item.label),
        meta: item.meta ? sanitizeVisibleAssistantText(item.meta) : item.meta,
        preview: item.preview
          ? sanitizeVisibleAssistantText(item.preview)
          : item.preview,
        title: item.title
          ? sanitizeVisibleAssistantText(item.title)
          : item.title,
      })),
    };
  }
  if (section.type === "related_links") {
    return {
      ...section,
      groups: section.groups.map((group) => ({
        ...group,
        label: sanitizeVisibleAssistantText(group.label),
        items: group.items.map((item) => ({
          ...item,
          label: sanitizeVisibleAssistantText(item.label),
          meta: item.meta ? sanitizeVisibleAssistantText(item.meta) : item.meta,
          preview: item.preview
            ? sanitizeVisibleAssistantText(item.preview)
            : item.preview,
          title: item.title
            ? sanitizeVisibleAssistantText(item.title)
            : item.title,
        })),
      })),
    };
  }
  if (section.type === "table") {
    return {
      ...section,
      columns: section.columns.map((column) => ({
        ...column,
        label: sanitizeVisibleAssistantText(column.label),
      })),
      rows: section.rows.map((row) => ({
        ...row,
        cells: row.cells.map((cell) => {
          if (typeof cell === "string") {
            return sanitizeVisibleAssistantText(cell);
          }
          return {
            ...cell,
            text: sanitizeVisibleAssistantText(cell.text),
            detail: cell.detail
              ? sanitizeVisibleAssistantText(cell.detail)
              : cell.detail,
            title: cell.title
              ? sanitizeVisibleAssistantText(cell.title)
              : cell.title,
          };
        }),
      })),
      footerText: section.footerText
        ? sanitizeVisibleAssistantText(section.footerText)
        : section.footerText,
      footerAction: sanitizeActionLink(section.footerAction),
    };
  }
  if (section.type === "chart") {
    return {
      ...section,
      chartTitle: section.chartTitle
        ? sanitizeVisibleAssistantText(section.chartTitle)
        : section.chartTitle,
      items: section.items.map((item) => ({
        ...item,
        label: sanitizeVisibleAssistantText(item.label),
      })),
    };
  }
  return section;
};

const collectSectionVisibleText = (
  section: AssistantPresentationSection,
): string[] => {
  if (section.type === "summary" || section.type === "formatted_body") {
    return [section.text];
  }
  if (section.type === "metric_grid") {
    return section.items.flatMap((item) => [
      item.label,
      item.value,
      item.detail ?? "",
    ]);
  }
  if (section.type === "checklist") {
    return section.items.flatMap((item) => [item.label, item.detail ?? ""]);
  }
  if (section.type === "result_cards") {
    return section.cards.flatMap((card) => [
      card.label,
      card.eyebrow ?? "",
      card.badge ?? "",
      card.meta ?? "",
      card.preview ?? "",
      card.primaryAction?.label ?? "",
      card.secondaryAction?.label ?? "",
      ...(card.detailRows?.flatMap((row) => [row.label, row.value]) ?? []),
    ]);
  }
  if (section.type === "confirmation") {
    return [section.titleText, section.summary, section.meta ?? ""];
  }
  if (section.type === "artifacts") {
    return section.items.flatMap((item) => [
      item.label,
      item.kind ?? "",
      item.badge ?? "",
      item.meta ?? "",
      item.preview ?? "",
      item.actionLabel ?? "",
    ]);
  }
  if (section.type === "task_proposal") {
    return [
      section.proposal.title,
      section.proposal.summary,
      section.proposal.question ?? "",
      section.proposal.startLabel ?? "",
      section.proposal.askInChatLabel ?? "",
      section.proposal.dismissLabel ?? "",
    ];
  }
  if (section.type === "task_thread") {
    return [
      section.thread.title,
      section.thread.stateLabel,
      section.thread.statusMessage ?? "",
      section.thread.detailsSummary ?? "",
      section.thread.degradedReason ?? "",
      ...(section.thread.events?.flatMap((event) => [
        event.label,
        event.state ?? "",
        event.summary ?? "",
        event.content ?? "",
      ]) ?? []),
    ];
  }
  if (section.type === "sources") {
    return section.items.flatMap((item) => [
      item.label,
      item.meta ?? "",
      item.preview ?? "",
      item.title ?? "",
    ]);
  }
  if (section.type === "related_links") {
    return section.groups.flatMap((group) => [
      group.label,
      ...group.items.flatMap((item) => [
        item.label,
        item.meta ?? "",
        item.preview ?? "",
        item.title ?? "",
      ]),
    ]);
  }
  if (section.type === "diagnostics") {
    return [
      section.diagnostics.title,
      section.diagnostics.summary,
      section.diagnostics.explanation ?? "",
      section.diagnostics.operatorGuidance ?? "",
    ];
  }
  if (section.type === "table") {
    return [
      ...section.columns.map((column) => column.label),
      ...section.rows.flatMap((row) =>
        row.cells.flatMap((cell) => {
          if (typeof cell === "string") {
            return [cell];
          }
          return [cell.text, cell.detail ?? "", cell.title ?? ""];
        }),
      ),
      section.footerText ?? "",
      section.footerAction?.label ?? "",
    ];
  }
  if (section.type === "chart") {
    return [
      section.chartTitle ?? "",
      ...section.items.map((item) => item.label),
    ];
  }
  return [];
};

const projectChecklistFromSummary = (
  text: string,
): { summaryText: string; checklist: AssistantPresentationSection | null } => {
  const normalized = String(text ?? "")
    .replace(/([a-z)])(\d+\.)/g, "$1\n$2")
    .replace(/\s+(\d+\.)\s+/g, "\n$1 ")
    .trim();
  const matches = Array.from(
    normalized.matchAll(/(?:^|\n)\s*(\d+)\.\s+([^\n]+?)(?=\n\s*\d+\.|\s*$)/g),
  );
  if (
    matches.length < 1 ||
    !/\b(?:priorit|readiness|next steps?|action items?)\b/i.test(normalized)
  ) {
    if (
      !/\b(?:priorit|readiness|next steps?|action items?|risk)\b/i.test(
        normalized,
      )
    ) {
      return { summaryText: text, checklist: null };
    }
    const candidates = normalized
      .replace(/^#+\s*/gm, "")
      .split(/(?:[.!?]\s+|\n+)/)
      .map((entry) => entry.trim().replace(/[:;,\s]+$/, ""))
      .filter(
        (entry) =>
          entry.length >= 24 &&
          !/^(sources?|actions?|current workspace)$/i.test(entry),
      )
      .slice(0, 5);
    if (candidates.length < 1) {
      return { summaryText: text, checklist: null };
    }
    return {
      summaryText: PROJECTED_CHECKLIST_SUMMARY_TEXT,
      checklist: {
        type: "checklist",
        id: "projected-priority-checklist",
        title: /priorit/i.test(normalized)
          ? "Recommended priorities"
          : "Recommended next checks",
        items: candidates.map((label, index) => ({
          id: `projected-priority-${index + 1}`,
          label: sanitizeVisibleAssistantText(label),
          status: "todo",
        })),
      },
    };
  }
  const firstIndex = matches[0]?.index ?? 0;
  const summaryText =
    stripDuplicatedChecklistHeading(normalized.slice(0, firstIndex)) ||
    PROJECTED_CHECKLIST_SUMMARY_TEXT;
  return {
    summaryText,
    checklist: {
      type: "checklist",
      id: "projected-priority-checklist",
      title: /priorit/i.test(normalized)
        ? "Recommended priorities"
        : "Recommended next checks",
      items: matches.map((match, index) => ({
        id: `projected-priority-${index + 1}`,
        label: sanitizeVisibleAssistantText(String(match[2] ?? "").trim()),
        status: "todo",
      })),
    },
  };
};

const isStudentsFamiliesPresentation = (
  model: AssistantPresentationModel,
): boolean => (
  model.routeFamily === "students-families" ||
  model.module === "students-families"
);

const isGenericProjectedChecklistSection = (
  section: AssistantPresentationSection,
): boolean => (
  section.type === "checklist" &&
  (
    /^(?:recommended\s+next\s+checks?|response\s+checks?|recommended\s+priorities)$/i
      .test(collapseWhitespace(section.title ?? "")) ||
    /^projected-/i.test(collapseWhitespace(String(section.id ?? "")))
  )
);

const sanitizeStudentsFamiliesPresentationSection = (
  section: AssistantPresentationSection,
): AssistantPresentationSection | null => {
  if (isGenericProjectedChecklistSection(section)) {
    return null;
  }
  if (section.type !== "summary") {
    return section;
  }
  const strippedText = stripDuplicatedChecklistHeading(section.text);
  return {
    ...section,
    text:
      strippedText === PROJECTED_CHECKLIST_SUMMARY_TEXT
        ? section.text
        : strippedText,
  };
};

export const validateAssistantPresentationStructure = (
  model: AssistantPresentationModel | null | undefined,
): AssistantPresentationQualityIssue[] => {
  if (!model?.sections?.length) {
    return [
      {
        code: "missing-presentation",
        severity: "error",
        sectionType: "presentation",
        message:
          "Assistant response is missing a structured presentation model.",
      },
    ];
  }
  const issues: AssistantPresentationQualityIssue[] = [];
  const sectionTypes = model.sections.map((section) => section.type);
  const kind = model.responseKind ?? null;
  if (kind) {
    const required = RESPONSE_KIND_REQUIRED_SECTIONS[kind] ?? [];
    for (const requiredType of required) {
      if (!sectionTypes.includes(requiredType)) {
        issues.push({
          code: `missing-section-${requiredType}`,
          severity: "error",
          sectionType: "presentation",
          message: `Response kind ${kind} requires a ${requiredType} section.`,
        });
      }
    }
    const anyOf = RESPONSE_KIND_ANY_OF_SECTIONS[kind] ?? [];
    if (
      anyOf.length > 0 &&
      !anyOf.some((type) => sectionTypes.includes(type))
    ) {
      issues.push({
        code: `missing-any-section-${anyOf.join("-or-")}`,
        severity: "error",
        sectionType: "presentation",
        message: `Response kind ${kind} requires at least one of: ${anyOf.join(", ")}.`,
      });
    }
  }
  if (sectionTypes[0] !== "summary") {
    issues.push({
      code: "summary-not-first",
      severity: "error",
      sectionType: "presentation",
      message: "Assistant presentation must start with a summary section.",
    });
  }
  const checklistTitles = model.sections
    .filter((section) => section.type === "checklist")
    .map((section) => normalizeHeadingComparison(section.title ?? ""))
    .filter(Boolean);
  const summaryTexts = model.sections
    .filter((section) => section.type === "summary")
    .map((section) => collapseWhitespace(section.text));
  for (const summaryText of summaryTexts) {
    const normalizedSummary = normalizeHeadingComparison(summaryText);
    const duplicatedTitle = checklistTitles.find((title) => (
      normalizedSummary === title ||
      normalizedSummary.startsWith(`${title}:`) ||
      normalizedSummary.startsWith(`${title}.`) ||
      normalizedSummary === `${title} are listed below`
    ));
    if (!duplicatedTitle) {
      continue;
    }
    issues.push({
      code: "duplicate-section-heading-copy",
      severity: "error",
      sectionType: "summary",
      excerpt: excerpt(summaryText),
      message:
        "Summary body repeats a checklist section title instead of carrying distinct response substance.",
    });
  }
  for (const section of model.sections) {
    for (const text of collectSectionVisibleText(section)) {
      issues.push(
        ...scanAssistantPresentationTextQuality(text, {
          sectionType: section.type,
          allowRawRoutes: section.type === "diagnostics",
        }),
      );
    }
  }
  return issues;
};

const mergeDuplicateSections = (
  sections: AssistantPresentationSection[],
  options: { responseKind?: AssistantResponseKind | null } = {},
): AssistantPresentationSection[] => {
  let mergedSources: AssistantPresentationSourcesSection | null = null;
  let mergedDiagnostics: AssistantPresentationDiagnosticsSection | null = null;
  const passthrough: AssistantPresentationSection[] = [];

  for (const section of sections) {
    if (section.type === "sources") {
      if (mergedSources) {
        mergedSources = {
          type: "sources",
          id: mergedSources.id ?? section.id,
          title: mergedSources.title ?? section.title,
          items: dedupeSourceItems([...mergedSources.items, ...section.items]),
        };
      } else {
        mergedSources = {
          type: "sources",
          id: section.id,
          title: section.title,
          items: dedupeSourceItems(section.items),
        };
      }
      continue;
    }
    if (section.type === "diagnostics") {
      if (mergedDiagnostics) {
        mergedDiagnostics = {
          type: "diagnostics",
          id: mergedDiagnostics.id ?? section.id,
          title: mergedDiagnostics.title ?? section.title,
          diagnostics: {
            summary: mergedDiagnostics.diagnostics.summary,
            title:
              mergedDiagnostics.diagnostics.title ?? section.diagnostics.title,
            explanation:
              mergedDiagnostics.diagnostics.explanation ??
              section.diagnostics.explanation ??
              null,
            operatorGuidance:
              mergedDiagnostics.diagnostics.operatorGuidance ??
              section.diagnostics.operatorGuidance ??
              null,
            targetRoute:
              mergedDiagnostics.diagnostics.targetRoute ??
              section.diagnostics.targetRoute ??
              null,
            defaultOpen:
              mergedDiagnostics.diagnostics.defaultOpen ||
              section.diagnostics.defaultOpen,
            displayMode:
              mergedDiagnostics.diagnostics.displayMode === "drawer" ||
              section.diagnostics.displayMode === "drawer"
                ? "drawer"
                : (mergedDiagnostics.diagnostics.displayMode ??
                  section.diagnostics.displayMode),
          },
        };
      } else {
        mergedDiagnostics = section;
      }
      continue;
    }
    if (section.type === "related_links") {
      passthrough.push({
        ...section,
        groups: dedupeRelatedLinks(section.groups),
      });
      continue;
    }
    passthrough.push(section);
  }

  if (mergedSources && mergedSources.items.length > 0) {
    passthrough.push(mergedSources);
  }
  if (mergedDiagnostics) {
    passthrough.push(mergedDiagnostics);
  }

  return passthrough.sort((left, right) => {
    const orderDelta =
      resolveSectionOrder(left.type, options.responseKind) -
      resolveSectionOrder(right.type, options.responseKind);
    if (orderDelta !== 0) {
      return orderDelta;
    }
    return String(left.id ?? "").localeCompare(String(right.id ?? ""));
  });
};

export const normalizeAssistantPresentation = (
  model: AssistantPresentationModel | null | undefined,
): AssistantPresentationModel | null => {
  if (!model?.sections?.length) {
    return null;
  }
  const studentsFamiliesPresentation = isStudentsFamiliesPresentation(model);
  const sections = mergeDuplicateSections(
    model.sections
      .filter(Boolean)
      .filter((section) => {
        if (section.type === "summary" || section.type === "formatted_body") {
          return collapseWhitespace(section.text).length > 0;
        }
        if (section.type === "table") {
          return section.columns.length > 0 && section.rows.length > 0;
        }
        if (section.type === "chart") {
          return section.items.length > 0;
        }
        if (section.type === "metric_grid" || section.type === "checklist") {
          return section.items.length > 0;
        }
        if (section.type === "result_cards") {
          return section.cards.length > 0;
        }
        if (section.type === "confirmation") {
          return (
            collapseWhitespace(section.titleText).length > 0 &&
            collapseWhitespace(section.summary).length > 0
          );
        }
        if (section.type === "artifacts") {
          return section.items.length > 0;
        }
        if (section.type === "sources") {
          return section.items.length > 0;
        }
        if (section.type === "related_links") {
          return section.groups.some((group) => group.items.length > 0);
        }
        return true;
      })
      .map((section) => sanitizePresentationSection(section))
      .map((section) =>
        studentsFamiliesPresentation
          ? sanitizeStudentsFamiliesPresentationSection(section)
          : section,
      )
      .filter((section): section is AssistantPresentationSection =>
        Boolean(section),
      ),
    { responseKind: model.responseKind },
  );

  if (sections.length === 0) {
    return null;
  }

  let normalizedSections = mergeDuplicateSections(
    [
      ...sections,
      ...(sections.some((section) => section.type === "sources")
        ? []
        : [DEFAULT_SOURCES_SECTION]),
    ],
    { responseKind: model.responseKind },
  );
  normalizedSections = removeDuplicateRouteActionTargets(normalizedSections);
  const structuredActionSurfaceAlreadyPresent = normalizedSections.some(
    (section) =>
      section.type === "table" ||
      section.type === "result_cards" ||
      section.type === "artifacts" ||
      section.type === "task_proposal" ||
      section.type === "task_thread" ||
      section.type === "related_links" ||
      section.type === "confirmation",
  );
  if (
    !studentsFamiliesPresentation &&
    !structuredActionSurfaceAlreadyPresent &&
    !normalizedSections.some((section) => section.type === "checklist")
  ) {
    const summaryIndex = normalizedSections.findIndex(
      (section) => section.type === "summary",
    );
    const summarySection = normalizedSections[summaryIndex];
    if (summarySection?.type === "summary") {
      const projected = projectChecklistFromSummary(summarySection.text);
      if (projected.checklist) {
        normalizedSections = mergeDuplicateSections(
          [
            ...normalizedSections.slice(0, summaryIndex),
            {
              ...summarySection,
              text: projected.summaryText,
            },
            projected.checklist,
            ...normalizedSections.slice(summaryIndex + 1),
          ],
          { responseKind: model.responseKind },
        );
      }
    }
  }

  return {
    version: "1.0.0",
    routeFamily: model.routeFamily ?? null,
    module: model.module ?? null,
    responseKind: model.responseKind ?? null,
    preferredArtifactType: model.preferredArtifactType ?? null,
    sections: normalizedSections,
  };
};

export const resolveAssistantPresentation = <
  TCitation,
  TAction extends AssistantActionBase,
>(
  message: AssistantMessage<TCitation, TAction>,
): AssistantPresentationModel | null => {
  const normalized = normalizeAssistantPresentation(message.presentation);
  if (normalized) {
    return normalized;
  }

  if (message.role === "user") {
    return null;
  }

  const sections: AssistantPresentationSection[] = [];
  const visibleText = stripVisibleAssistantMachinePayload(message.content);
  if (visibleText) {
    sections.push({
      type: "summary",
      text: visibleText,
    });
  }

  const citations = Array.isArray(message.citations) ? message.citations : [];
  if (citations.length > 0) {
    sections.push({
      type: "sources",
      items: citations.map((citation, index) => toSourceItem(citation, index)),
    });
  }

  return normalizeAssistantPresentation({
    version: "1.0.0",
    sections,
  });
};
